import React, { FC, useCallback, useMemo, useState } from 'react';
import { reportEvent } from 'Shared/analytics';
import { useAppContext, useReportsContext } from 'QuoteApp/services/providers';
import {
  GetDetailsResponseType,
  ThirdPartyReports,
} from 'QuoteApp/services/endpoints/GetDetails';
import { Disclosures } from 'QuoteApp/services/endpoints/GetDisclosures';
import { MissingDataModal } from './MissingDataModal';
import { Report } from '../Report';
import { useReportRunner } from '../useReportRunner';
import { ReportStatus } from '../types';
import {
  getMissingOrInvalidLicenseCount,
  getMissingOrInvalidVinCount,
  getReportStatus,
  haveAllRequiredReportsRun,
} from '../utils';

export const ClueReport: FC = () => {
  const { details } = useAppContext();
  const {
    acknowledgeDisclosure,
    changeReportInProgress,
    reportInProgress,
    reports,
    reportsDisabled,
  } = useReportsContext();

  const [showMissingDataModal, setShowMissingDataModal] = useState<boolean>(
    false
  );

  const { runReport, messages } = useReportRunner('clue');

  const openMissingDataModal = useCallback(() => {
    if (!showMissingDataModal) {
      reportEvent('aqui-tpr-missing-VIN-DL-prompt', {
        missingVINCount: getMissingOrInvalidVinCount(details),
        missingDLCount: getMissingOrInvalidLicenseCount(details),
      });
      setShowMissingDataModal(true);
    }
  }, [details, showMissingDataModal]);

  const closeMissingDataModal = useCallback(() => {
    setShowMissingDataModal(false);
  }, []);

  const clueStatus = useMemo(() => {
    if (reportInProgress === ThirdPartyReports.Clue) {
      return ReportStatus.Pending;
    } else if (reports.clue) {
      return getReportStatus(reports.clue.status);
    }
    return ReportStatus.Disabled;
  }, [reports.clue, reportInProgress]);

  const canRunClue = useMemo<boolean>(() => {
    if (reportInProgress) {
      return false;
    } else if (!haveAllRequiredReportsRun(ThirdPartyReports.Clue, reports)) {
      return false;
    } else if (
      getMissingOrInvalidLicenseCount(details) > 0 ||
      getMissingOrInvalidVinCount(details) > 0
    ) {
      return true;
    }
    return reports.clue?.status === 'runnable';
  }, [reportInProgress, reports, details]);

  const runClue = useCallback(
    async (newDetails: GetDetailsResponseType) => {
      try {
        changeReportInProgress(ThirdPartyReports.Clue);
        await acknowledgeDisclosure(Disclosures.Reports);
        if (
          getMissingOrInvalidLicenseCount(newDetails) > 0 ||
          getMissingOrInvalidVinCount(newDetails) > 0
        ) {
          openMissingDataModal();
        }

        const newClueReport = newDetails.commands.reports.find(
          r => r.reportName === ThirdPartyReports.Clue
        );

        if (newClueReport?.status !== 'runnable') {
          changeReportInProgress(null);
          return;
        }

        await runReport(newClueReport, true);
        reportEvent('clue-success');
        changeReportInProgress(null);
      } catch (e) {
        changeReportInProgress(null);
      }
    },
    [
      changeReportInProgress,
      acknowledgeDisclosure,
      openMissingDataModal,
      runReport,
    ]
  );

  return reports.clue ? (
    <>
      <Report
        displayName="C.L.U.E. Report"
        messages={messages}
        enabled={!reportsDisabled && canRunClue}
        onClick={() => {
          runClue(details);
        }}
        status={clueStatus}
        type="clue"
      />
      <MissingDataModal
        show={showMissingDataModal}
        onSuccess={(newDetails: GetDetailsResponseType) => {
          closeMissingDataModal();
          runClue(newDetails);
        }}
        onCancel={() => {
          changeReportInProgress(null);
          closeMissingDataModal();
        }}
      />
    </>
  ) : null;
};
